import { CRAYO_ENTRY, money, formatHours, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const PRICING = "https://www.captions.ai/pricing";
const HELP_SUBSCRIPTIONS = "https://captions.ai/help/docs/subscriptions";

export const CAPTIONS_AI_CONTENT: CompareContent = {
  slug: "crayo-vs-captions-ai",
  competitorSlug: "captions-ai",
  title: "Crayo vs Captions",
  seoTitle: "Crayo vs Captions (Captions AI): Pricing and Credits Compared",
  seoDescription:
    "Crayo vs Captions with dated prices. Captions burns credits without an export and sells one plan per platform. Crayo is one browser plan, 1 credit per finished clip.",
  cardVerdict: "Better than Crayo for mobile apps and avatars, behind on metering and the editor.",
  bestFor: "Clippers weighing a phone app against a browser editor",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip podcasts and streams in a browser and want one subscription with published yearly prices, credits spent only on finished clips, and a timeline editor on the same ${money(hobby.monthly)} plan. Captions is the better pick if you edit on a phone, want AI avatars and dubbing, or clip inside a mobile app. The evidence is below.`,
  pickCrayoIf: [
    "You want credits that only leave your account when a clip is finished: Crayo charges 1 credit per AutoClip clip or AI edit, while Captions deducts credits whether or not the project is exported, and a Mirage video costs 8 credits per second.",
    `You want one subscription that works everywhere you log in: Captions sells desktop and iPhone as separate subscriptions, and Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for the one browser app.`,
    "You want ranked moments before you spend, and an API: Find Viral Moments lists up to 50 ranked moments and a credit is spent only when you cut, and the 34-endpoint API with webhooks is on every plan, while Captions sells its Mirage API as a separate product.",
  ],
  pickThemIf: [
    "You edit on a phone. Captions ships iOS and Android apps with Clips, face tracking and a per-clip score on uploads up to 3 hours. Crayo is a browser app.",
    "You want AI avatars, AI twins, or dubbing in 30+ languages. Crayo has no avatars and no voice cloning.",
    "You want 100+ caption styles and a brand with 20M users behind it. Crayo ships 32 caption styles.",
  ],
  wins: [
    {
      claim: "Crayo charges a credit only for a finished clip; Captions charges credits whether or not you export.",
      evidence:
        "The Captions help docs state credits are deducted regardless of whether the project is exported or saved, checked Oct 8, 2026. A third-party pricing check on Sep 25, 2026 put a Mirage video at 8 credits per second, so one 15-second Mirage video spends 120 of the 500 credits on Max. Crayo spends 1 credit per finished clip, and Find Viral Moments lists ranked moments before any credit is spent.",
      href: HELP_SUBSCRIPTIONS,
    },
    {
      claim: "Crayo's timeline editor is on every plan; Captions edits through a chat-based editor and a simple editor.",
      evidence:
        "Captions describes a chat-based AI Edit flow and a simplified editor on its features pages, checked Oct 8, 2026. Crayo's editor has a timeline, camera track, keyframes, zooms, transitions, speed ramps, 32 caption styles with keyword highlight and 56 spoken languages on every plan.",
      href: "https://crayo.ai/features/auto-clip",
    },
    {
      claim: "Crayo is one subscription in the browser; Captions is one subscription per platform.",
      evidence:
        "Captions' pricing page states its listed prices are iOS plans, and the help docs say the desktop app and the iPhone app need separate subscriptions as of Oct 8, 2026. A Crayo plan is one login that works in any browser.",
      href: PRICING,
    },
    {
      claim: "Crayo publishes monthly and yearly prices; Captions publishes monthly only.",
      evidence:
        `As of Oct 8, 2026, captions.ai/pricing describes annual billing as discounted with no figure. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year, about ${money(hobby.yearlyPerMonth)} a month, on the pricing page.`,
      href: PRICING,
    },
    {
      claim: "Crayo includes a public API and View Tracker on every plan; Captions sells its API separately and lists no creator tracking.",
      evidence:
        "Captions' features pages list no creator tracking, no AI image generator and no story generator as of Oct 8, 2026, and the Mirage API is sold as a separate product. Crayo's 34-endpoint API with webhooks and View Tracker are on Hobby, Clipper and Pro, and so are its story, text and voiceover generators, each costing 1 credit from the same pool.",
      href: "https://crayo.ai/developers",
    },
  ],
  concessions: [
    {
      claim: "Captions has iOS and Android apps; Crayo is a browser app.",
      evidence:
        "Captions' homepage and pricing page sell the product as a mobile-first app with a separate desktop app, checked Oct 8, 2026. Crayo runs in the browser; this page makes no claim about a Crayo mobile app.",
      href: "https://www.captions.ai",
    },
    {
      claim: "Captions' Clips feature is mature: face tracking, a per-clip score and 30+ clips from uploads up to 3 hours.",
      evidence:
        "The Create Clips page states uploads up to 3 hours or 10 GB, 30 or more clips per upload, a score per clip and a camera that keeps faces centered and follows the speaker, checked Oct 8, 2026.",
      href: "https://captions.ai/features/create-clips",
    },
    {
      claim: "Captions has AI avatars, AI twins and dubbing in 30+ languages.",
      evidence:
        "Captions advertises AI avatars and AI twins on its homepage and dubbing in 30+ languages in its Jul 2, 2026 CapCut comparison post. Crayo has no avatars, no voice cloning and no dubbing.",
      href: "https://captions.ai/blog/captions-vs-capcut",
    },
    {
      claim: "Captions has 100+ caption styles and a free tier; Crayo has 32 styles and no free plan.",
      evidence:
        "Captions lists 100+ animated caption styles and a Free plan with 60 to 200 lifetime credits in the help docs as of Oct 8, 2026. Crayo has no free plan and no trial.",
      href: HELP_SUBSCRIPTIONS,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes (${formatHours(hobby.exportMinutes)}). Every finished clip is 1 credit, and nothing is spent on a clip you do not cut.`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Max",
      cost: "$24.99/mo on iOS; annual price not published",
      usage:
        "Not published. Captions' Clips takes uploads up to 3 hours and returns 30 or more clips, but Captions does not state the credit cost of a Clips run, so the credits used by four sources and 32 clips cannot be computed. For scale, one 15-second Mirage video is 120 of the 500 credits.",
      note: "Credits are charged whether or not the project is exported, per the help docs.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 20-minute single-speaker talking-head video owned by Crayo",
    plans: "Captions Max and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Captions publishes monthly prices only, as iOS plans, and sells desktop and iPhone as separate subscriptions. Credits are deducted whether or not you export, and only Frontier 4x can buy credit top-ups. The pricing FAQ says unused credits roll over up to 3x the monthly amount; the help doc says they do not roll over, so ask support which applies before buying. Refund and cancellation terms are not on the pricing page as of Oct 8, 2026.",
  faqs: [
    {
      question: "Is Crayo cheaper than Captions for clipping?",
      answer:
        `It depends on the billing term. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} workflow credits, and one credit is one finished clip. Captions Max is $24.99 a month for 500 credits on one platform, so on monthly billing Captions Max is the lower sticker price and on quarterly or yearly billing Crayo Hobby is. Captions deducts credits whether or not you export, and the $9.99 Basic plan appears only in the help docs and watermarks AI Creator and AI Edit exports. Captions does not publish the credit cost of a Clips run or a plain caption edit, so a per-clip comparison is not possible from its pages. What is published is the Mirage video rate of 8 credits per second, which puts one 15-second AI video at 120 credits.`,
    },
    {
      question: "How much does the Captions app cost?",
      answer:
        "As of Oct 8, 2026, captions.ai/pricing lists Free, Max at $24.99 a month for 500 credits, Frontier at $69.99 for 1,400 credits, Frontier 2x at $139.99 for 2,800, Frontier 4x at $279.99 for 5,600, and Enterprise by contact. The help docs add a Basic plan at $9.99 for 200 credits with watermarked AI exports. The page states these are iOS plans; the desktop app is a separate subscription. Annual prices are described as discounted but no figures are published.",
    },
    {
      question: "What is a good Captions AI alternative for clipping long videos?",
      answer:
        `Crayo AutoClip takes a YouTube or TikTok link or a file up to 3 hours, finds the strongest moments with a virality ranking, reframes faces, captions and exports 1 to 20 clips per run at 1 credit per clip, and Find Viral Moments lists up to 50 ranked moments before any credit is spent. Captions' Clips feature does the same job inside its apps with a per-clip score and 30 or more clips per upload. The difference is the bill: Captions deducts credits whether or not you export, and Crayo charges only for finished clips, with ${hobby.credits} credits a month on Hobby.`,
    },
    {
      question: "Does Crayo have a mobile app like Captions?",
      answer:
        "Crayo is a browser app. You open crayo.ai, paste a YouTube or TikTok link or upload a file up to 4 GB, and clip, caption and export at 1080p from the browser. Captions is built around its iOS and Android apps, with a desktop app sold as a separate subscription, and its pricing page lists iOS plans. If you only ever edit on a phone, Captions fits that workflow. If you want one subscription that works on any machine with a browser, and a timeline editor with keyframes, zooms and speed ramps rather than a chat-based editor, Crayo does that without a second plan.",
    },
    {
      question: "Does Crayo have a free plan like Captions?",
      answer:
        `No. Crayo has no free plan and no trial; the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload your file. The entry plan is Hobby at ${money(hobby.monthly)} a month. Captions lists a Free plan that shows 0 credits on the pricing table, while its help docs describe 60 to 200 lifetime credits, one caption template and Mirage videos up to 15 seconds.`,
    },
    {
      question: "Do Captions credits roll over?",
      answer:
        "Captions' own pages disagree as of Oct 8, 2026. The pricing FAQ says unused credits roll over up to 3x the monthly allotment. The subscriptions help doc says credits do not roll over. Credits are also deducted whether or not the project is exported or saved. Crayo credits reset each month with no rollover, and one-time top-up packs are separate, so neither product lets unused monthly credits accumulate indefinitely.",
    },
    {
      question: "Does Crayo have AI avatars or dubbing like Captions?",
      answer:
        "No. Crayo has no AI avatars, no AI twins, no voice cloning and no dubbing. Captions advertises all of these, with dubbing in 30+ languages. Crayo's AI voiceover uses hundreds of stock voices, and Crayo transcribes 56 spoken languages for captions with 32 styles and keyword highlight. If an avatar or a dubbed version of your clip is the deciding feature, Captions wins that row.",
    },
    {
      question: "Captions app vs CapCut vs Crayo: which should a short-form creator pick?",
      answer:
        "Captions is the pick for phone-first editing with avatars and dubbing. CapCut is covered on its own page at crayo.ai/compare/crayo-vs-capcut. Crayo is the pick for clippers who want one browser subscription with published yearly prices, credits spent only on finished clips, ranked moments from a pasted link, and a timeline editor on every plan. Captions published its own CapCut comparison on Jul 2, 2026 and names Descript, Opus Clip, VEED and Submagic as alternatives; it does not mention Crayo.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Captions prices checked Oct 8, 2026: Max $24.99/mo for 500 credits, Frontier $69.99/mo for 1,400, Basic $9.99/mo in the help docs only; annual prices not published.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-capcut", "crayo-vs-submagic", "crayo-vs-opus-clip"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "crayo vs captions",
    "captions ai alternative",
    "captions app vs capcut",
    "captions ai vs opus clip",
    "captions app cost",
    "captions ai review reddit",
  ],
};
